'use client';

import type {
  CategoryFilterState,
  CategoryFilterUpdate,
} from '@eliadmin/core/src/components/categories/category-query-state';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

import { AttributeFilter, isFilterAttribute } from './attribute-filter';
import { byPosition, CheckList, FilterGroup } from './filter-group';
import { RangeFilter } from './range-filter';

/**
 * The drawer groups in the Figma order – Cena, Výrobca, Dostupnosť – then the category attributes,
 * each only when its template setting is on and the category has values. Sub-categories are tiles
 * on the page (`categories_in_filter` is off). No „Stav produktu“ group: core never sends status
 * filters to the products API (`categoryProductsQueryOptions`), so it could not change the results.
 */
export function FilterGroups({
  filter,
  onChange,
}: {
  filter: CategoryFilterState;
  onChange: (update: CategoryFilterUpdate) => void;
}) {
  const { t } = useTranslations();
  const category = useCategoryStore(s => s.category);
  const settings = useGlobalStore(s => s.templateSettings.category);
  const currency = useGlobalStore(s => s.generalLayout?.currency);

  // Whole currency units around the category's price range; 0 in the filter means „no bound“.
  const lower = Math.floor(category.price?.min ?? 0);
  const upper = Math.ceil(category.price?.max ?? lower);
  const bound = (value: number) => Math.min(upper, Math.max(lower, value));

  const tagGroups = [
    {
      key: 'manufacturers',
      title: t('text175'),
      enabled: settings?.manufacturers_in_filter?.enabled,
      tags: category.manufacturers,
      selected: filter.manufacturers,
      update: (id: number): CategoryFilterUpdate => ({ manufacturer: id }),
    },
    {
      key: 'availabilities',
      title: t('text339'),
      enabled: settings?.availability_in_filter?.enabled,
      tags: category.availabilities,
      selected: filter.availabilities,
      update: (id: number): CategoryFilterUpdate => ({ availability: id }),
    },
  ];
  const attributes = settings?.attributes_in_filter?.enabled
    ? (category.attributes ?? []).filter(isFilterAttribute)
    : [];

  return (
    <>
      {settings?.price_in_filter?.enabled && upper > lower ? (
        <FilterGroup title={t('text340')}>
          <RangeFilter
            min={lower}
            max={upper}
            value={[bound(filter.price_min || lower), bound(filter.price_max || upper)]}
            onChange={([from, to]) =>
              onChange({ price_min: from > lower ? from : 0, price_max: to < upper ? to : 0 })
            }
            labels={[`${t('text340')} ${t('text811')}`, `${t('text340')} ${t('text86')}`]}
            unit={currency}
          />
        </FilterGroup>
      ) : null}
      {tagGroups.map(group =>
        group.enabled && group.tags?.length ? (
          <FilterGroup key={group.key} title={group.title}>
            <CheckList
              values={byPosition(group.tags)}
              isChecked={id => group.selected.includes(id)}
              onToggle={tag => onChange(group.update(tag.id))}
            />
          </FilterGroup>
        ) : null,
      )}
      {attributes.map(attribute => (
        <FilterGroup
          key={attribute.id}
          title={attribute.name}
          // `collapse` opens the group as in the core filter; a group with a selection opens too.
          defaultOpen={
            !!attribute.collapse || filter.attributes.some(item => item.id === attribute.id)
          }
        >
          <AttributeFilter
            attribute={attribute}
            selection={filter.attributes}
            onChange={onChange}
            singleRadio={!!settings?.allow_only_one_value_in_radio_filter?.enabled}
          />
        </FilterGroup>
      ))}
    </>
  );
}
